import React from 'react';
import { Sparkles, X } from 'lucide-react';
import { ageBandLabel, dismissAgeBandPicker, setAgeBand, useAgeBand } from '../../utils/ageBand';

/**
 * Gentle, dismissible one-time prompt that asks for the learner's age band.
 * Persists per device. Once chosen or dismissed, it stays hidden.
 *
 * Compact variant (always shown in Settings, etc) renders without the dismiss
 * affordance and skips the "we won't ask again" copy.
 */
function AgeBandPicker({ compact = false, onChange = null }) {
  const [band, setBand, hasPicked] = useAgeBand();

  if (!compact && hasPicked) return null;

  const choose = (next) => {
    setBand(next);
    if (typeof onChange === 'function') onChange(next);
  };

  const card = {
    borderRadius: 18,
    padding: compact ? 16 : 22,
    background: 'var(--color-bg-card)',
    border: '1px solid var(--color-border)',
    boxShadow: '0 4px 18px var(--color-shadow)',
    /** Extra bottom margin on home so the card clears phone gesture areas when scrolled into view */
    margin: compact ? 0 : '12px 0 calc(32px + env(safe-area-inset-bottom, 0px))',
    position: 'relative',
  };
  const titleRow = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 6,
    color: 'var(--color-text)',
  };
  const title = {
    fontSize: compact ? 16 : 18,
    fontWeight: 800,
    margin: 0,
    color: 'var(--color-text)',
  };
  const sub = {
    fontSize: 14,
    lineHeight: 1.5,
    margin: '0 0 14px',
    color: 'var(--color-text)',
    opacity: 0.75,
  };
  const buttons = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
  };
  const btnBase = {
    flex: '1 1 160px',
    padding: '12px 14px',
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    border: '1px solid var(--color-border)',
    background: 'var(--color-bg)',
    color: 'var(--color-text)',
    textAlign: 'left',
  };
  const btnActive = {
    background: 'var(--color-primary)',
    color: '#fff',
    borderColor: 'var(--color-primary)',
    boxShadow: '0 2px 10px var(--color-shadow)',
  };
  const microRow = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginTop: 12,
  };
  const microBtn = {
    background: 'transparent',
    border: 'none',
    color: 'var(--color-text)',
    opacity: 0.7,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    padding: 4,
  };
  const dismissBtn = {
    position: 'absolute',
    top: 10,
    right: 10,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--color-text)',
    opacity: 0.55,
    padding: 4,
    borderRadius: 8,
  };

  return (
    <section
      style={card}
      role="region"
      aria-labelledby="age-band-picker-title"
    >
      {!compact ? (
        <button
          type="button"
          aria-label="Maybe later"
          style={dismissBtn}
          onClick={() => dismissAgeBandPicker()}
        >
          <X size={18} aria-hidden />
        </button>
      ) : null}
      <div style={titleRow}>
        <Sparkles size={20} aria-hidden style={{ color: 'var(--color-primary)' }} />
        <h2 id="age-band-picker-title" style={title}>
          {compact ? 'Learner age band' : 'Pick a learner age range'}
        </h2>
      </div>
      <p style={sub}>
        {compact
          ? 'Pages and activity wording will adapt to this band.'
          : 'We use this to highlight age-appropriate lessons first. You can switch any time in Settings.'}
      </p>
      <div style={buttons}>
        {[
          { v: '5-8', desc: 'Short prompts, simple words.' },
          { v: '9-12', desc: 'Richer vocabulary, deeper challenges.' },
        ].map(({ v, desc }) => {
          const active = band === v;
          return (
            <button
              key={v}
              type="button"
              style={active ? { ...btnBase, ...btnActive } : btnBase}
              onClick={() => choose(v)}
              aria-pressed={active}
            >
              <div style={{ fontSize: 16, fontWeight: 800 }}>{ageBandLabel(v)}</div>
              <div style={{ fontSize: 13, opacity: active ? 0.92 : 0.7, marginTop: 4 }}>{desc}</div>
            </button>
          );
        })}
      </div>
      {!compact ? (
        <div style={microRow}>
          <button type="button" style={microBtn} onClick={() => dismissAgeBandPicker()}>
            Maybe later
          </button>
        </div>
      ) : null}
    </section>
  );
}

export default AgeBandPicker;
